import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useStore } from '@/lib/store';
import { closeDialog, openDialog, overlayStore, type DialogSpec } from '@/lib/overlays';
import { ESC, TRAP, useEscape, useFocusTrap } from '@/lib/keys';
import { usePresence } from '@/lib/hooks';
import { errorMessage } from '@/lib/api';
import { Icon } from './Icon';
import type { IconName } from './icons';

// Centred dialogs. Open one with openDialog({ label, render: (close) => ... }) from anywhere.

/** Moves focus into a freshly opened layer: an element React already focused, then [data-autofocus], then the layer. */
export function focusInto(root: HTMLElement | null): void {
  if (!root) return;
  if (root.contains(document.activeElement) && document.activeElement !== root) return;
  const target = root.querySelector<HTMLElement>('[data-autofocus]');
  (target ?? root).focus({ preventScroll: true });
}

export function DialogHost() {
  const spec = useStore(overlayStore, (s) => s.dialog);
  const [shownSpec, setShownSpec] = useState<(DialogSpec & { key: number }) | null>(spec);
  const { mounted, shown } = usePresence(!!spec, 300);
  const ref = useRef<HTMLElement>(null);
  const returnTo = useRef<HTMLElement | null>(null);

  if (spec && spec !== shownSpec) setShownSpec(spec);

  useEffect(() => {
    if (!spec) return;
    if (!returnTo.current) {
      const active = document.activeElement;
      returnTo.current = active instanceof HTMLElement && active !== document.body ? active : null;
    }
    const t = setTimeout(() => focusInto(ref.current), 40);
    return () => clearTimeout(t);
  }, [spec]);

  useEffect(() => {
    if (spec) return;
    const back = returnTo.current;
    returnTo.current = null;
    // Give focus back to whatever opened the dialog, unless the dialog moved it somewhere new on purpose.
    if (back && back.isConnected && (document.activeElement === document.body || ref.current?.contains(document.activeElement))) {
      back.focus({ preventScroll: true });
    }
  }, [spec]);

  useEscape(!!spec, ESC.dialog, () => {
    closeDialog();
  });
  useFocusTrap(ref, !!spec, TRAP.dialog);

  const current = spec ?? shownSpec;
  if (!mounted || !current) return null;
  const close = () => closeDialog(current.key);
  return (
    <div className={['layer', 'open', shown && spec ? 'show' : ''].filter(Boolean).join(' ')} aria-hidden={spec ? undefined : true}>
      <div className="scrim" onMouseDown={close} />
      <section
        ref={ref}
        key={current.key}
        className={['dialog', current.size ?? ''].filter(Boolean).join(' ')}
        role="dialog"
        aria-modal="true"
        aria-label={current.label}
        tabIndex={-1}
        id="dlg"
      >
        {current.render(close)}
      </section>
    </div>
  );
}

export function DialogHead({
  title,
  sub,
  glyph,
  onClose,
}: {
  title: ReactNode;
  sub?: ReactNode;
  /** A small navy tile with an icon before the title. */
  glyph?: IconName;
  onClose: () => void;
}) {
  return (
    <div className="dlg-h">
      {glyph ? (
        <span className="ai-glyph">
          <Icon name={glyph} />
        </span>
      ) : null}
      <div className="grow">
        <h2>{title}</h2>
        {sub ? <p>{sub}</p> : null}
      </div>
      <button type="button" className="icon-btn sm" aria-label="Close" data-tip="Close" onClick={onClose}>
        <Icon name="x" />
      </button>
    </div>
  );
}

export function DialogBody({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={['dlg-b', className].filter(Boolean).join(' ')}>{children}</div>;
}

export function DialogFoot({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={['dlg-f', className].filter(Boolean).join(' ')}>{children}</div>;
}

/** The dialog's way out without saving. Every dialog uses this one style. */
export function DialogCancel({ onClick, children = 'Cancel', disabled }: { onClick: () => void; children?: ReactNode; disabled?: boolean }) {
  return (
    <button type="button" className="btn btn-ghost" onClick={onClick} disabled={disabled}>
      {children}
    </button>
  );
}

/** Closes a dialog that has nothing left to save, such as one showing a result. */
export function DialogDone({ onClick, children = 'Done', autoFocus }: { onClick: () => void; children?: ReactNode; autoFocus?: boolean }) {
  return (
    <button type="button" className="btn btn-tile" onClick={onClick} data-autofocus={autoFocus || undefined}>
      {children}
    </button>
  );
}

/**
 * Asks a yes-or-no question. Resolves true when confirmed.
 * With `action`, the dialog runs it on confirm, stays open while it works and shows its error if it fails.
 */
export function confirmDialog(opts: {
  title: string;
  body?: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  icon?: IconName;
  /** Styles the confirm button as destructive. */
  danger?: boolean;
  action?: () => Promise<unknown>;
}): Promise<boolean> {
  return new Promise((resolve) => {
    let answered = false;
    openDialog({
      label: opts.title,
      onClose: () => {
        if (!answered) resolve(false);
      },
      render: (close) => (
        <ConfirmBody
          opts={opts}
          close={close}
          onConfirmed={() => {
            answered = true;
            resolve(true);
          }}
        />
      ),
    });
  });
}

function ConfirmBody({
  opts,
  close,
  onConfirmed,
}: {
  opts: Parameters<typeof confirmDialog>[0];
  close: () => void;
  onConfirmed: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const confirm = async () => {
    if (!opts.action) {
      onConfirmed();
      close();
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await opts.action();
      onConfirmed();
      close();
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  };
  return (
    <>
      <DialogHead title={opts.title} sub={opts.body} onClose={close} />
      <DialogFoot>
        {error ? (
          <span className="note err" role="alert">
            <Icon name="warn" />
            {error}
          </span>
        ) : null}
        <span className="spacer" />
        <DialogCancel onClick={close} disabled={busy}>
          {opts.cancelLabel ?? 'Cancel'}
        </DialogCancel>
        <button
          type="button"
          className={opts.danger ? 'btn btn-danger' : 'btn btn-primary'}
          data-autofocus
          disabled={busy}
          aria-busy={busy || undefined}
          onClick={() => void confirm()}
        >
          {opts.icon ? <Icon name={opts.icon} /> : null}
          {opts.confirmLabel}
        </button>
      </DialogFoot>
    </>
  );
}
